import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from 'lucide-react';
import { useInAppPurchase } from '@/hooks/useInAppPurchase';
import { useStripePrices } from '@/hooks/useStripePrices';
import { getStoredRedditClickId } from '@/lib/redditPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const PremiumPaywall = ({ userId, onClose, feature = 'generic' }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const { isNative, products, purchasing, error: iapError, purchase, PRODUCT_IDS } = useInAppPurchase(userId);

  // Surface IAP errors as toasts
  useEffect(() => {
    if (iapError) toast.error(iapError);
  }, [iapError]);

  const currentLang = i18n.language?.substring(0, 2) || 'en';
  const currencySymbol = currentLang === 'it' ? '€' : currentLang === 'es' ? '€' : '$';
  const webCurrency = currentLang === 'it' || currentLang === 'es' ? 'eur' : 'usd';
  const stripePrices = useStripePrices(webCurrency);

  // One product, one price — read live from the store on native and from
  // Stripe on web. Neither source is ever second-guessed with a hardcoded
  // amount: if the price hasn't arrived we show a placeholder and keep the
  // Buy button disabled rather than name a number we aren't sure of.
  const lifetimeProduct = products.find(p => p.period === 'lifetime');
  const iapLoading = isNative && products.length === 0;
  const webLifetime = stripePrices.lifetime != null
    ? `${currencySymbol}${stripePrices.lifetime.toFixed(2)}`
    : null;
  const lifetimePrice = isNative ? (lifetimeProduct?.price || null) : webLifetime;
  const priceReady = Boolean(lifetimePrice);
  const displayPrice = lifetimePrice || '…';

  // Updated: Notes and Events are now free. Only advanced features are premium.
  const features = [
    t('premium.featureReminders'),
    t('premium.featureAI'),
    t('premium.advancedNotes'),
    t('premium.unlimitedSuggestions'),
  ];

  const handleUpgrade = async () => {
    setLoading(true);
    let handedOffToSuccess = false;
    const handOffToSuccess = () => {
      if (handedOffToSuccess) return;
      handedOffToSuccess = true;
      onClose?.();
      const returnPath = location.pathname || '/dashboard';
      navigate(`/premium/success?source=iap&return=${encodeURIComponent(returnPath)}`, { replace: true });
    };

    try {
      // On native iOS/Android, use In-App Purchase
      if (isNative) {
        const result = await purchase(PRODUCT_IDS.LIFETIME, { userId, onApprovalQueued: handOffToSuccess });
        if (handedOffToSuccess) return;
        if (!result) {
          toast.error(t('premium.errorCheckout'));
          return;
        }
        handOffToSuccess();
        return;
      }

      // On web, use Stripe checkout
      const response = await axios.post(`${API}/subscription/create-checkout`, {
        user_id: userId,
        origin_url: window.location.origin,
        language: currentLang,
        plan_type: 'lifetime',
        click_id: getStoredRedditClickId() || undefined
      });
      const url = response?.data?.checkout_url;
      if (url) {
        window.location.href = url;
        return;
      }
      throw new Error('no checkout URL');
    } catch (error) {
      if (handedOffToSuccess) return;
      const errorMsg = error?.response?.data || error?.message || error;
      console.error('Error creating checkout session:', errorMsg);
      toast.error(t('premium.errorCheckout'));
    } finally {
      if (!handedOffToSuccess) setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200 font-manrope">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('premium.maybeLater') || 'Close'}
          className="absolute top-3 right-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-stone-700 shadow-sm ring-1 ring-black/5 hover:bg-white hover:text-stone-900 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Calm hero — minimal */}
        <div className="bg-white border-b border-stone-100 p-6 text-center">
          <div className="w-16 h-16 bg-stone-100 rounded-full mx-auto mb-4" />
          <h2 className="text-2xl font-serif font-medium text-stone-900 mb-1">
            {t('premium.heroTitle')}
          </h2>
          <p className="text-sm text-stone-600">
            {t('premium.heroSubtitle')}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* The single offer. No plan picker: there is one product, bought
              once. */}
          <div
            data-testid="lifetime-plan"
            className="w-full rounded-xl p-4 border-2 border-[#3284f1] bg-blue-50 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="text-base font-semibold text-stone-900">{t('premium.lifetimeLabel')}</span>
                <p className="text-[11px] text-stone-500 mt-1">{t('premium.lifetimeTagline')}</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-extrabold text-stone-900">{displayPrice}</span>
              </div>
            </div>
          </div>

          {/* What's included */}
          <div>
            <h4 className="text-sm font-semibold text-stone-900 mb-3">{t('premium.includesTitle')}</h4>
            <div className="space-y-2">
              {features.map((feature, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-amber-500 text-xs">✦</span>
                  <span className="text-sm text-stone-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Billed amount — the most clear and conspicuous pricing element on
              this screen (App Store Guideline 3.1.2(c)). The one-time terms
              follow in a smaller, lighter, subordinate line. */}
          <div className="text-center">
            <p className="text-xs font-medium text-stone-500 leading-none">
              {t('premium.lifetimeOneTime')}
            </p>
            <p data-testid="billed-amount" className="text-3xl font-extrabold text-stone-900 leading-none mt-1.5 tracking-tight">
              {displayPrice}
            </p>
            <p className="text-[11px] text-stone-500 mt-2 leading-snug">
              {t('premium.billingTerms', { price: displayPrice })}
            </p>
          </div>

          {/* CTA Button */}
          <div className="space-y-2">
            <button
              data-testid="upgrade-premium-btn"
              onClick={handleUpgrade}
              disabled={loading || purchasing || iapLoading || !priceReady}
              className="w-full h-12 rounded-none bg-[#3284f1] text-white font-medium hover:bg-[#2a6bc7] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {(loading || purchasing) ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('premium.upgradeNow')
              )}
            </button>
          </div>

          {/* Close button */}
          {onClose && (
            <button
              onClick={onClose}
              className="w-full text-center text-sm text-stone-500 hover:text-stone-700 transition-colors"
            >
              {t('premium.maybeLater')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PremiumPaywall;
